import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '../../lib/queryKeys';
import { policyApi, SimulationImpact } from '../../services/api/policy';

function LoadingSpinner({ className }: { className?: string }) {
  return (
    <div className={`inline-block border-2 border-current border-t-transparent rounded-full animate-spin ${className || 'w-4 h-4'}`} />
  );
}

function ChevronIcon({ className, expanded }: { className?: string; expanded: boolean }) {
  return (
    <svg
      className={`${className || 'w-3.5 h-3.5'} transition-transform duration-200 ${expanded ? 'rotate-90' : ''}`}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

function ImpactRoleRow({ impact }: { impact: SimulationImpact }) {
  const [expanded, setExpanded] = useState(false);
  const gained = impact.gained.length;
  const lost = impact.lost.length;

  return (
    <div>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between py-2 px-3 rounded-md hover:bg-neutral-100 dark:hover:bg-slate-700/50 transition-colors text-left"
      >
        <div className="flex items-center gap-2">
          <ChevronIcon className="w-3 h-3 text-neutral-400 dark:text-slate-500" expanded={expanded} />
          <span className="text-sm font-medium text-neutral-800 dark:text-slate-200">{impact.role}</span>
        </div>
        <div className="flex items-center gap-2">
          {gained > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
              +{gained} gained
            </span>
          )}
          {lost > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
              -{lost} lost
            </span>
          )}
        </div>
      </button>

      {expanded && (
        <div className="ml-7 mr-3 mb-2 space-y-0.5">
          {impact.gained.map((g) => (
            <div
              key={g.action}
              className="flex items-center gap-2 px-2.5 py-1 rounded bg-green-50 dark:bg-green-900/20 text-xs"
            >
              <span className="text-green-600 dark:text-green-400 font-semibold">+</span>
              <span className="text-green-800 dark:text-green-300 font-mono">{g.label}</span>
            </div>
          ))}
          {impact.lost.map((l) => (
            <div
              key={l.action}
              className="flex items-center gap-2 px-2.5 py-1 rounded bg-red-50 dark:bg-red-900/20 text-xs"
            >
              <span className="text-red-600 dark:text-red-400 font-semibold">-</span>
              <span className="text-red-800 dark:text-red-300 font-mono line-through">{l.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export interface PolicySimulationProps {
  cedarPolicy: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export function PolicySimulation({ cedarPolicy, onConfirm, onCancel }: PolicySimulationProps) {
  // THUB-299: was three useState values plus a hand-rolled `cancelled` flag in the effect
  // cleanup. That flag existed so a slow simulation for a policy the admin had already edited
  // away from could not land and be displayed as the impact of the CURRENT policy text -- on a
  // screen whose entire job is "what permissions will this take away", showing the wrong
  // policy's impact is the worst available failure.
  //
  // Keying on the policy text gets that for free and more strictly than the flag did: each draft
  // is its own cache entry, so a late result lands in the draft it belongs to and is not merely
  // ignored -- it cannot reach the current draft's slot at all.
  const simulation = useQuery({
    queryKey: queryKeys.policy.simulation(cedarPolicy),
    queryFn: () => policyApi.simulatePolicy(cedarPolicy),
  });

  const loading = simulation.isPending;
  const result = simulation.data ?? null;
  // Preserve the original message contract: the error's own message, else the default.
  const error = simulation.error
    ? ((simulation.error as { message?: string }).message || 'Simulation failed')
    : null;

  const changedImpacts = result?.impacts.filter(
    (i) => i.gained.length > 0 || i.lost.length > 0
  ) || [];

  const hasLostPermissions = changedImpacts.some((i) => i.lost.length > 0);
  const totalGained = changedImpacts.reduce((sum, i) => sum + i.gained.length, 0);
  const totalLost = changedImpacts.reduce((sum, i) => sum + i.lost.length, 0);

  return (
    <div className="bg-neutral-50 dark:bg-slate-800 border border-neutral-200 dark:border-slate-700 rounded-lg overflow-hidden my-2">
      {/* Header */}
      <div className="px-4 py-3 flex items-center justify-between border-b border-neutral-200 dark:border-slate-700">
        <span className="text-xs font-semibold text-neutral-500 dark:text-slate-400 uppercase tracking-wider">
          {hasLostPermissions ? '⚠ Policy Impact' : 'Policy Impact'}
        </span>
        {!loading && changedImpacts.length > 0 && (
          <div className="flex items-center gap-1.5">
            {totalGained > 0 && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-semibold bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                +{totalGained}
              </span>
            )}
            {totalLost > 0 && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-semibold bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
                -{totalLost}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="px-4 py-3">
        {loading && (
          <div className="flex items-center gap-3 py-2">
            <LoadingSpinner className="w-4 h-4 text-indigo-500" />
            <span className="text-sm text-neutral-500 dark:text-slate-400">
              Analyzing impact...
            </span>
          </div>
        )}

        {error && (
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        )}

        {result && !loading && (
          <>
            {/* Summary */}
            <p className="text-sm text-neutral-600 dark:text-slate-400 mb-2">
              {result.summary}
            </p>

            {/* Changed roles (expandable) */}
            {changedImpacts.length > 0 ? (
              <div className="border border-neutral-200 dark:border-slate-700 rounded-md divide-y divide-neutral-200 dark:divide-slate-700">
                {changedImpacts.map((impact) => (
                  <ImpactRoleRow key={impact.role} impact={impact} />
                ))}
              </div>
            ) : (
              <p className="text-xs text-neutral-400 dark:text-slate-500 italic">
                No permission changes detected.
              </p>
            )}
          </>
        )}
      </div>

      {/* Buttons */}
      {!loading && !error && (
        <div className="px-4 py-2.5 border-t border-neutral-200 dark:border-slate-700 flex items-center justify-end gap-2">
          <button
            onClick={onCancel}
            className="px-3 py-1.5 text-xs font-medium rounded-md text-neutral-600 dark:text-slate-400 hover:bg-neutral-200 dark:hover:bg-slate-700 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className={`px-3 py-1.5 text-xs font-medium rounded-md text-white transition-colors ${
              hasLostPermissions
                ? 'bg-amber-600 hover:bg-amber-700'
                : 'bg-primary-600 hover:bg-primary-700'
            }`}
          >
            Confirm & Apply
          </button>
        </div>
      )}
    </div>
  );
}

export default PolicySimulation;
